import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

/**
 * ThemeContext — app-wide light/dark theme.
 *
 * `preference` is one of 'system' | 'light' | 'dark' and is persisted to the
 * user profile (PUT /auth/me theme) with a localStorage mirror so the choice
 * survives reloads and syncs across devices. `resolvedTheme` is the concrete
 * 'light' | 'dark' actually applied — 'system' resolves to the OS preference,
 * defaulting to DARK unless the OS explicitly prefers light.
 *
 * Dark is applied by toggling the existing `.home-dark-scope` engine (which
 * already darkens reused light components) on the app content wrapper.
 */
const STORAGE_KEY = 'seerio_theme_pref';
const ThemeContext = createContext({ preference: 'system', resolvedTheme: 'dark', setPreference: () => {}, toggle: () => {} });

function resolvePref(pref) {
  if (pref === 'light') return 'light';
  if (pref === 'dark') return 'dark';
  // system → follow OS, defaulting to dark when the OS has no explicit light preference
  if (typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [preference, setPreferenceState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'system'; } catch { return 'system'; }
  });
  const [resolvedTheme, setResolvedTheme] = useState(() => resolvePref(preference));

  // Adopt the server-persisted preference once the user profile loads.
  useEffect(() => {
    const serverPref = user?.theme_preference;
    if (serverPref && ['system', 'light', 'dark'].includes(serverPref) && serverPref !== preference) {
      setPreferenceState(serverPref);
      try { localStorage.setItem(STORAGE_KEY, serverPref); } catch { /* noop */ }
    }
  }, [user?.theme_preference]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep resolvedTheme in sync with preference + live OS changes.
  useEffect(() => {
    setResolvedTheme(resolvePref(preference));
    if (preference !== 'system' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => setResolvedTheme(resolvePref('system'));
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [preference]);

  // Expose the resolved theme on <body> so PORTALED shadcn content (Select
  // dropdowns, Dialogs) — which render outside the .home-dark-scope wrapper —
  // can pick up dark design tokens. See index.css body[data-app-theme="dark"].
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.setAttribute('data-app-theme', resolvedTheme);
    }
  }, [resolvedTheme]);

  // Brief window during which the app shell (header/sidebar) fades colours
  // instead of snapping. Skipped on first mount so initial paint stays crisp.
  const firstPaint = useRef(true);
  useEffect(() => {
    if (firstPaint.current) { firstPaint.current = false; return undefined; }
    if (typeof document === 'undefined') return undefined;
    document.body.classList.add('theme-fade');
    const t = setTimeout(() => document.body.classList.remove('theme-fade'), 400);
    return () => clearTimeout(t);
  }, [resolvedTheme]);

  const setPreference = useCallback((pref) => {
    if (!['system', 'light', 'dark'].includes(pref)) return;
    setPreferenceState(pref);
    try { localStorage.setItem(STORAGE_KEY, pref); } catch { /* noop */ }
    api.put('/auth/theme', { theme_preference: pref }).catch(() => { /* non-blocking */ });
  }, []);

  const toggle = useCallback(() => {
    setPreference(resolvedTheme === 'dark' ? 'light' : 'dark');
  }, [resolvedTheme, setPreference]);

  return (
    <ThemeContext.Provider value={{ preference, resolvedTheme, setPreference, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
